/**
 * Live connection to one OpenClaw Gateway.
 *
 * Transport, handshake and reconnect are owned by `GatewayProtocolClient` from
 * the official `@openclaw/gateway-client` package; this class supplies the parts
 * that are environment-specific — the browser socket, the Ed25519 device
 * identity, credential persistence, and the policy for what a connect failure
 * means to the user.
 */
import {
  DEFAULT_PREAUTH_HANDSHAKE_TIMEOUT_MS,
  GatewayBrowserDeviceAuthLifecycle,
  GatewayProtocolClient,
  classifyGatewayConnectFailure,
  formatConnectErrorMessage,
  readConnectErrorDetailCode,
  readConnectPairingRequiredMessage,
  readPairingConnectErrorDetails,
  shouldPauseGatewayReconnect,
  shouldRetryGatewayWithDeviceToken,
  type ConnectParams,
  type EventFrame,
  type GatewayBrowserDeviceAuthPlan,
  type HelloOk,
} from "@openclaw/gateway-client/browser";
import {
  CLIENT_DISPLAY_NAME,
  CLIENT_ID,
  CLIENT_MODE,
  CLIENT_VERSION,
  CONNECT_ROLE,
  CONNECT_SCOPES,
  MAX_PROTOCOL,
  MIN_PROTOCOL,
  RECONNECT,
} from "./constants";
import {
  createDeviceTokenStore,
  loadUnsupportedClientFields,
  saveUnsupportedClientFields,
  type ConnectionSettings,
} from "./credentials";
import { loadOrCreateDeviceIdentity, signWithIdentity } from "./identity";
import { randomId } from "../lib/id";

export type ConnectionPhase =
  | "idle"
  | "connecting"
  /** Device is known to the Gateway but a human still has to approve it. */
  | "pairing"
  | "connected"
  | "reconnecting"
  /** Terminal: reconnecting cannot fix this without the user changing something. */
  | "failed";

/** What the Gateway told us about the pending approval for this device. */
export type PairingInfo = {
  /** Identifies the pending request in `openclaw devices list/approve`. */
  requestId?: string;
  reason?: string;
  requestedRole?: string;
  requestedScopes?: string[];
};

export type ConnectionState = {
  phase: ConnectionPhase;
  /** User-facing explanation for `pairing` and `failed`. */
  message?: string;
  /** What the user should do about it, when the Gateway tells us. */
  remediation?: string;
  deviceId?: string;
  serverVersion?: string;
  scopes?: string[];
  pairing?: PairingInfo;
  /** Consecutive attempts that never reached a successful handshake. */
  attempts?: number;
  /** Technical detail (close code/reason) for a connect that keeps failing. */
  diagnostic?: string;
  /**
   * The Gateway refused this browser origin. Terminal until someone changes the
   * Gateway's config, and fixable with one command, so it gets its own screen.
   */
  originBlocked?: boolean;
};

export type ConnectionListeners = {
  onState: (state: ConnectionState) => void;
  onEvent: (event: EventFrame) => void;
  /** Fires after each successful handshake, including reconnects. */
  onReady: (hello: HelloOk) => void;
};

/**
 * How often to retry while a device waits for approval.
 *
 * The protocol's default for a pairing rejection is to pause the reconnect
 * loop, but for a messenger the whole point is that approving on the Gateway
 * makes the app come alive on its own. A slow poll against the user's own
 * Gateway costs nothing and saves them a manual retry.
 */
const PAIRING_POLL_MS = 5_000;

/** Attempts to allow before explaining a connect that never succeeds. */
const DIAGNOSTIC_AFTER_ATTEMPTS = 2;

/** `client` fields the handshake cannot omit, so a rejection naming one is fatal. */
const REQUIRED_CLIENT_FIELDS = new Set(["id", "version", "platform", "mode"]);

/**
 * Recognises the Gateway's browser-origin refusal.
 *
 * The Gateway checks the `Origin` header of the WebSocket upgrade against
 * `gateway.controlUi.allowedOrigins` and refuses anything else. That check is
 * the reason a random web page cannot drive somebody's agent, so the app treats
 * it as a configuration step to explain rather than an error to retry.
 *
 * It arrives as free text — sometimes in the close reason, sometimes in the
 * connect rejection — so matching the phrase is the only way to spot it.
 */
export function isOriginRejection(text: string | undefined | null): boolean {
  return typeof text === "string" && /origin not allowed|allowedOrigins/i.test(text);
}

/** Turns a failed connect into something the user can act on. */
function describeFailedConnect(
  context: { code: number; reason: string; socketOpened: boolean; connectRequestSent: boolean },
  url: string,
): { message: string; remediation?: string; diagnostic?: string } {
  const diagnostic = context.reason
    ? `Verbindung geschlossen (Code ${context.code}: ${context.reason})`
    : `Verbindung geschlossen (Code ${context.code})`;

  if (!context.socketOpened) {
    return {
      message: `Das Gateway unter ${url} ist nicht erreichbar.`,
      remediation:
        "Check the address and port, whether OpenClaw is running, and whether this device is on " +
        "the same network. With wss:// the certificate has to be valid.",
      diagnostic,
    };
  }
  if (!context.connectRequestSent) {
    return {
      message: "Verbunden, aber das Gateway hat den Handshake nicht begonnen.",
      remediation: "Antwortet unter dieser Adresse wirklich ein OpenClaw-Gateway?",
      diagnostic,
    };
  }
  return {
    message: "Das Gateway hat die Anmeldung abgelehnt.",
    remediation: context.reason
      ? `Grund laut Gateway: ${context.reason}`
      : "Check the token, and whether this device has been approved.",
    diagnostic,
  };
}

function isSecureEndpoint(url: string): boolean {
  try {
    const parsed = new URL(url);
    if (parsed.protocol === "wss:") {
      return true;
    }
    const host = parsed.hostname;
    return host === "localhost" || host === "127.0.0.1" || host === "::1" || host === "[::1]";
  } catch {
    return false;
  }
}

/** Accepts what a human is likely to paste and returns a ws:// or wss:// URL. */
export function normalizeGatewayUrl(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) {
    throw new Error("Gateway-Adresse fehlt.");
  }
  const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `ws://${trimmed}`;
  const parsed = new URL(withScheme);
  if (parsed.protocol === "http:") {
    parsed.protocol = "ws:";
  } else if (parsed.protocol === "https:") {
    parsed.protocol = "wss:";
  }
  if (parsed.protocol !== "ws:" && parsed.protocol !== "wss:") {
    throw new Error("Die Adresse muss mit ws:// oder wss:// beginnen.");
  }
  // The Gateway upgrades at the root; a trailing slash is noise in the origin
  // scope used to key stored device tokens.
  if (parsed.pathname === "/") {
    parsed.pathname = "";
  }
  return parsed.toString().replace(/\/$/, "");
}

export class GatewayConnection {
  private readonly client: GatewayProtocolClient<GatewayBrowserDeviceAuthPlan>;
  private readonly lifecycle: GatewayBrowserDeviceAuthLifecycle;
  private readonly listeners: ConnectionListeners;
  private readonly settings: ConnectionSettings;
  private readonly trustedEndpoint: boolean;
  private state: ConnectionState = { phase: "idle" };
  /** One bounded retry with a stored device token, per the protocol's auth rules. */
  private deviceTokenRetryUsed = false;
  private pendingDeviceTokenRetry = false;
  private everConnected = false;
  private stopped = false;
  /** Consecutive attempts that never reached a successful handshake. */
  private attempts = 0;
  /** What `handleConnectFailure` decided, consumed by the next `resolveClose`. */
  private pendingDecision: { stop: boolean; delayMs?: number } | null = null;
  /** Optional `client` fields this Gateway build rejects. */
  private readonly unsupportedClientFields: Set<string>;

  constructor(settings: ConnectionSettings, listeners: ConnectionListeners) {
    this.settings = settings;
    this.listeners = listeners;
    this.trustedEndpoint = isSecureEndpoint(settings.url);
    this.unsupportedClientFields = new Set(loadUnsupportedClientFields(settings.url));
    this.lifecycle = new GatewayBrowserDeviceAuthLifecycle({
      loadIdentity: async () => {
        const identity = await loadOrCreateDeviceIdentity();
        return {
          deviceId: identity.deviceId,
          publicKey: identity.publicKey,
          sign: (payload: string) => signWithIdentity(identity, payload),
        };
      },
      tokenStore: createDeviceTokenStore(settings.url),
    });

    this.client = new GatewayProtocolClient<GatewayBrowserDeviceAuthPlan>({
      createSocket: (handlers) => {
        const socket = new WebSocket(this.settings.url);
        socket.onopen = () => handlers.open();
        socket.onmessage = (event) => {
          if (typeof event.data === "string") {
            handlers.message(event.data);
          }
        };
        socket.onclose = (event) => handlers.close(event.code, event.reason);
        socket.onerror = () => handlers.error(new Error("WebSocket-Fehler"));
        return {
          isOpen: () => socket.readyState === WebSocket.OPEN,
          send: (data) => socket.send(data),
          close: (code, reason) => socket.close(code, reason),
        };
      },
      createRequestId: () => randomId(),
      buildConnectPlan: ({ nonce, challengeTs }) => this.buildPlan(nonce, challengeTs),
      buildConnectParams: (plan) =>
        ({
          minProtocol: MIN_PROTOCOL,
          maxProtocol: MAX_PROTOCOL,
          client: this.clientInfo(),
          role: CONNECT_ROLE,
          scopes: plan.scopes,
          caps: [],
          ...(plan.auth ? { auth: plan.auth } : {}),
          ...(plan.device ? { device: plan.device } : {}),
          locale: navigator.language,
          userAgent: `${CLIENT_ID}/${CLIENT_VERSION}`,
        }) satisfies ConnectParams,
      onConnectHello: (hello, context) => {
        void this.lifecycle.acceptHello(hello, context.plan);
        this.deviceTokenRetryUsed = false;
        this.pendingDeviceTokenRetry = false;
        this.everConnected = true;
        this.attempts = 0;
        this.setState({
          phase: "connected",
          deviceId: context.plan.identity?.deviceId,
          serverVersion: hello.server?.version,
          scopes: hello.auth?.scopes,
        });
        this.listeners.onReady(hello);
      },
      onConnectFailure: (error, context) => this.handleConnectFailure(error, context.plan),
      resolveClose: (context) => {
        if (this.stopped) {
          return { retry: false, notify: false };
        }
        // A connect rejection already decided its own policy in onConnectFailure.
        //
        // Read that decision from our own field, never from
        // `context.connectFailure`: the protocol client also populates that on a
        // raw transport error (`handleSocketError`), with no reconnect delay
        // attached. Treating a missing delay as "stop" would end the reconnect
        // loop for good on every unreachable host, TLS failure or dropped WiFi.
        const decision = this.pendingDecision;
        this.pendingDecision = null;
        if (decision) {
          return decision.stop
            ? { retry: false, notify: true }
            : { retry: true, notify: false, reconnectDelayMs: decision.delayMs };
        }

        // A transport drop after a good handshake is normal; keep the app live.
        if (context.helloReceived || this.everConnected) {
          if (this.state.phase !== "failed") {
            this.setState({ ...this.state, phase: "reconnecting" });
          }
          return { retry: true, notify: true };
        }

        // Never handshaked. The Gateway also encodes a pairing rejection in the
        // close reason, so a close that arrives without a structured error still
        // tells us the device is merely waiting for approval.
        const fromCloseReason = readConnectPairingRequiredMessage(context.reason);
        if (fromCloseReason) {
          this.notePairing({ ...fromCloseReason });
          return { retry: true, notify: false, reconnectDelayMs: PAIRING_POLL_MS };
        }

        // Retrying a refused origin only produces the same refusal: stop and
        // hand the user the one command that changes the answer.
        if (isOriginRejection(context.reason)) {
          this.setState({
            ...this.state,
            phase: "failed",
            originBlocked: true,
            message: "The Gateway does not accept connections from this address.",
            diagnostic: context.reason,
          });
          return { retry: false, notify: true };
        }

        // Otherwise say what is actually failing instead of an endless "connecting".
        this.attempts += 1;
        this.setState({
          ...this.state,
          phase: "connecting",
          attempts: this.attempts,
          ...(this.attempts >= DIAGNOSTIC_AFTER_ATTEMPTS
            ? describeFailedConnect(context, this.settings.url)
            : {}),
        });
        return { retry: true, notify: true };
      },
      onEvent: (event) => this.listeners.onEvent(event),
      // The WebSocket constructor itself can throw — a blocked port, or a URL the
      // browser refuses — and that never reaches resolveClose.
      onSocketFactoryError: (error) => {
        if (this.everConnected || this.state.phase === "pairing") {
          return;
        }
        this.setState({
          ...this.state,
          phase: "failed",
          message: `The browser cannot open the address ${this.settings.url}.`,
          remediation:
            "Check the scheme, host and port. Some ports the browser blocks outright.",
          diagnostic: error.message,
        });
      },
      onConnectError: (error) => {
        if (this.everConnected || this.state.phase === "failed" || this.state.phase === "pairing") {
          return;
        }
        // Keep any diagnostic resolveClose already produced; a bare transport
        // error message on its own tells the user nothing useful.
        this.setState({
          ...this.state,
          phase: "connecting",
          ...(this.state.diagnostic ? {} : { diagnostic: error.message }),
        });
      },
      handshake: { mode: "fallback", timeoutMs: DEFAULT_PREAUTH_HANDSHAKE_TIMEOUT_MS },
      reconnect: { ...RECONNECT },
    });
  }

  /**
   * Client identity for the handshake.
   *
   * `connect.params.client` is a closed object on the Gateway, so a field a
   * given build does not know about fails the whole connect rather than being
   * ignored. Only long-standing fields are sent by default, and anything a
   * Gateway still rejects is dropped for good (see `noteUnsupportedClientField`)
   * — this has to work against whatever build the user happens to be running.
   *
   * Dropping `deviceFamily` is signature-safe: both sides feed the same absent
   * value into the v3 device-auth payload.
   */
  /**
   * Builds the connect plan, upgrading the scope grant when it is too narrow.
   *
   * A stored device token pins the connection to the scopes it was issued for
   * (`resolveGatewayConnectScopes` prefers them over the defaults), which is
   * right for reconnects but would silently keep an existing install on an old,
   * narrower grant forever. When the stored grant no longer covers what the app
   * needs, the token is dropped so the handshake asks for the full set again —
   * the Gateway then answers with a scope-upgrade approval.
   */
  private async buildPlan(
    nonce: string | null,
    challengeTs: number | null | undefined,
  ): Promise<GatewayBrowserDeviceAuthPlan> {
    const request = {
      client: this.clientInfo(),
      role: CONNECT_ROLE,
      defaultScopes: CONNECT_SCOPES,
      token: this.settings.token,
      pendingDeviceTokenRetry: this.pendingDeviceTokenRetry,
      trustedDeviceTokenRetry: this.trustedEndpoint,
      nonce,
      challengeTs,
    };
    const plan = await this.lifecycle.buildPlan(request);
    if (CONNECT_SCOPES.every((scope) => plan.scopes.includes(scope))) {
      return plan;
    }
    // Re-plan without the narrow token. Its scopes are baked into the signed
    // payload, so this has to happen before signing, not after.
    await this.lifecycle.clearStoredToken(plan);
    return await this.lifecycle.buildPlan(request);
  }

  private clientInfo(): ConnectParams["client"] {
    const info: Record<string, unknown> = {
      id: CLIENT_ID,
      displayName: CLIENT_DISPLAY_NAME,
      version: CLIENT_VERSION,
      platform: "web",
      mode: CLIENT_MODE,
    };
    for (const field of this.unsupportedClientFields) {
      delete info[field];
    }
    return info as ConnectParams["client"];
  }

  /**
   * Learns that this Gateway rejects one optional `client` field.
   *
   * Returns true when a retry is worth making. Required fields are never
   * dropped: without them the connect is invalid anyway, so a rejection naming
   * one is a different problem and must surface instead of looping.
   */
  private noteUnsupportedClientField(message: string | undefined): boolean {
    if (!message?.includes("invalid connect params") || !message.includes("/client")) {
      return false;
    }
    const match = /unexpected property '([^']+)'/.exec(message);
    const field = match?.[1];
    if (!field || REQUIRED_CLIENT_FIELDS.has(field) || this.unsupportedClientFields.has(field)) {
      return false;
    }
    this.unsupportedClientFields.add(field);
    // Remember across launches so an older Gateway costs one failed connect
    // once, not one on every start.
    saveUnsupportedClientFields(this.settings.url, [...this.unsupportedClientFields]);
    return true;
  }

  private handleConnectFailure(
    error: { message?: string; details?: unknown },
    plan: GatewayBrowserDeviceAuthPlan,
  ) {
    // An older Gateway rejecting an optional client field is a compatibility
    // problem, not an auth one: drop the field and retry at once.
    if (this.noteUnsupportedClientField(error.message)) {
      return this.decide(
        { closeCode: 4006, closeReason: "client-field-unsupported" },
        { stop: false, delayMs: 100 },
      );
    }

    const details = error.details;
    const pairing =
      readPairingConnectErrorDetails(details) ??
      // Older or differently-configured Gateways may only put the pairing fact in
      // the rejection message, so fall back to parsing that.
      readConnectPairingRequiredMessage(error.message);

    // The device is registered but a human has not approved it yet. This is the
    // normal first-run path: keep polling so approval brings the app up by itself.
    if (pairing) {
      this.notePairing(pairing, plan.identity?.deviceId);
      return this.decide(
        { closeCode: 4001, closeReason: "pairing-required" },
        { stop: false, delayMs: PAIRING_POLL_MS },
      );
    }

    // A shared token that the Gateway no longer accepts can still be recoverable
    // via the device token it issued earlier — but only on a trusted endpoint.
    const retryWithDeviceToken = shouldRetryGatewayWithDeviceToken({
      retryBudgetUsed: this.deviceTokenRetryUsed,
      currentDeviceToken: plan.selectedAuth.resolvedDeviceToken,
      explicitToken: this.settings.token,
      storedToken: plan.selectedAuth.storedToken,
      trustedEndpoint: this.trustedEndpoint,
      errorDetails: details,
    });
    if (retryWithDeviceToken) {
      this.deviceTokenRetryUsed = true;
      this.pendingDeviceTokenRetry = true;
      return this.decide(
        { closeCode: 4002, closeReason: "retry-with-device-token" },
        { stop: false, delayMs: 250 },
      );
    }
    this.pendingDeviceTokenRetry = false;

    const terminal = shouldPauseGatewayReconnect({
      details,
      deviceTokenRetryPending: false,
      tokenMismatchIsTerminal: true,
      protocolMismatchIsTerminal: true,
      clientVersionMismatchIsTerminal: true,
    });

    const classified = classifyGatewayConnectFailure({
      details,
      message: error.message ?? null,
    });

    if (terminal || isOriginRejection(error.message)) {
      const originBlocked = isOriginRejection(error.message);
      this.setState({
        phase: "failed",
        ...(originBlocked ? { originBlocked: true } : {}),
        message: originBlocked
          ? "The Gateway does not accept connections from this address."
          : classified.userMessage ||
            formatConnectErrorMessage({ message: error.message, details }),
        remediation: originBlocked ? undefined : classified.remediation,
        deviceId: plan.identity?.deviceId,
        ...(originBlocked ? { diagnostic: error.message } : {}),
      });
      return this.decide(
        { closeCode: 4003, closeReason: readConnectErrorDetailCode(details) ?? "connect-failed" },
        { stop: true },
      );
    }

    this.setState({
      ...this.state,
      phase: this.everConnected ? "reconnecting" : "connecting",
      message: classified.userMessage,
      remediation: classified.remediation,
    });
    return this.decide(
      { closeCode: 4004, closeReason: "connect-retry" },
      { stop: false, delayMs: 2_000 },
    );
  }

  /**
   * Records a connect-failure decision and shapes it for the protocol client.
   * `resolveClose` reads it back, so the two always agree on whether the
   * reconnect loop should keep going.
   */
  private decide(
    close: { closeCode: number; closeReason: string },
    decision: { stop: boolean; delayMs?: number },
  ) {
    this.pendingDecision = decision;
    return {
      ...close,
      ...(decision.stop ? { stop: true } : { reconnectDelayMs: decision.delayMs }),
    };
  }

  /**
   * Records that this device is waiting for approval.
   *
   * The request id is the part the user actually needs — it is what they pass to
   * `openclaw devices approve` — so it is carried through even when it only
   * arrived in a close reason rather than in structured error details.
   */
  private notePairing(
    pairing: {
      requestId?: string;
      reason?: string;
      deviceId?: string;
      requestedRole?: string;
      requestedScopes?: string[];
    },
    fallbackDeviceId?: string,
  ): void {
    const requestId = pairing.requestId ?? this.state.pairing?.requestId;
    this.setState({
      ...this.state,
      phase: "pairing",
      message:
        "This device has to be approved once in OpenClaw. After that the app connects on its own.",
      remediation: undefined,
      diagnostic: undefined,
      deviceId: pairing.deviceId ?? fallbackDeviceId ?? this.state.deviceId,
      pairing: {
        ...(requestId ? { requestId } : {}),
        ...(pairing.reason ? { reason: pairing.reason } : {}),
        ...(pairing.requestedRole ? { requestedRole: pairing.requestedRole } : {}),
        ...(pairing.requestedScopes ? { requestedScopes: pairing.requestedScopes } : {}),
      },
    });
  }

  private setState(next: ConnectionState): void {
    this.state = next;
    this.listeners.onState(next);
  }

  getState(): ConnectionState {
    return this.state;
  }

  /** Gateway origin; used to scope per-Gateway compatibility knowledge. */
  get url(): string {
    return this.settings.url;
  }

  start(): void {
    this.stopped = false;
    this.attempts = 0;
    this.pendingDecision = null;
    this.setState({ phase: "connecting" });
    this.client.start();
  }

  stop(): void {
    this.stopped = true;
    this.client.stop();
    this.setState({ phase: "idle" });
  }

  get connected(): boolean {
    return this.client.connected;
  }

  request<T = unknown>(method: string, params?: unknown): Promise<T> {
    return this.client.request<T>(method, params);
  }

  /** Brings a backgrounded PWA back online immediately instead of waiting out the backoff. */
  nudge(): void {
    if (!this.stopped && !this.client.connected && !this.client.connecting) {
      this.client.resetReconnectBackoff(RECONNECT.initialMs);
    }
  }

  /**
   * Reconnects immediately — the pairing screen's "check now".
   *
   * `resetReconnectBackoff` only changes the *next* delay and `start()` returns
   * early while a reconnect sleep is still pending, so neither one interrupts an
   * in-flight wait. Stopping first cancels that sleep, which is what makes the
   * retry actually happen now instead of whenever the timer was due.
   */
  retryNow(): void {
    this.stopped = false;
    this.attempts = 0;
    this.pendingDecision = null;
    this.client.stop();
    this.client.start();
  }
}
